import { KampaayDataProvider } from 'services/api/dataProvider'
import { BaseProduct } from 'services/api/entities/catalog/baseProduct/types/internal/baseProduct-models'
import { Experience } from 'services/api/entities/catalog/experience/types/internal/experience-models'
import { Highlight } from 'services/api/entities/catalog/mainProduct/types/internal/mainProduct-models'
import {
  OptionGroup,
  OptionItem,
} from 'services/api/entities/catalog/optionedProduct/types/internal/optionedProduct-models'
import { APIPriceRange } from 'services/api/entities/catalog/types'
import { Upselling } from 'services/api/entities/catalog/upselling/types/internal/upselling-models'
import { Customer } from 'services/api/entities/crm/pmi'
import {
  parseCsvFields,
  parseMultilangUpsellingNames,
} from 'services/utils/multilangParseWrite'

export const BASE_PRODUCT_HEADERS = [
  'ID',
  'Name',
  'Description',
  'Default Cost',
  'End User',
  'PMI',
]

export const formatBaseProductCsv =
  (_dataProvider?: KampaayDataProvider) => (record: BaseProduct) => ({
    ID: record.id,
    ...parseCsvFields({ Name: record.name, Description: record.description }),
    priceRanges: record.pricing.map(formatPriceRange),
  })

export const formatBusinessUserCsv =
  (_dataProvider?: KampaayDataProvider) => (record: Customer) => {
    const { name, id, externalUserId, ...rest } = record
    return {
      id,
      name,
      ...rest,
    }
  }

const BASE_OPTION = {
  Name: ' ',
  'Max selection': ' ',
  'Item name': ' ',
  'Item description': ' ',
}

export const getItem = (item: Omit<OptionItem, 'isPriced' | 'component'>) => ({
  ...parseCsvFields({
    'Item name': item.name,
    'Item description': item.description,
  }),
})

export const formatOptions = (options: OptionGroup[]) => {
  const mapped = options.map((optGrp) =>
    optGrp.items.map((itm, idx) =>
      idx === 0
        ? {
            ...BASE_OPTION,
            ...parseCsvFields({
              Name: optGrp.name,
              Description: optGrp.description,
            }),
            ...getItem(itm),
          }
        : { ...BASE_OPTION, ...getItem(itm) }
    )
  )
  return mapped
}

export const formatExperience =
  (_dataProvider?: KampaayDataProvider) => (exp: Experience) => {
    const {
      images,
      upsellingItems,
      upsellingItemsFull,
      pricing,
      highlights,
      name,
      description,
      advanceReservationMin,
      ...rest
    } = exp
    return {
      ...rest,
      ...parseCsvFields({ name, description }),
      'Booking time-limit': advanceReservationMin,
      highlights: formatHighlights(highlights),
      upselling: parseMultilangUpsellingNames(exp.upsellingItemsFull),
      options: formatOptions(exp.options),
      pricing: pricing.map(formatPriceRange),
    }
  }

export const formatHighlights = (highlights: Highlight[]) =>
  highlights.map(({ preview, content }) => parseCsvFields({ preview, content }))

export const formatExperienceUpselling =
  (_dataProvider?: KampaayDataProvider) =>
  ({ name, description, minQuantity, ...record }: Upselling) => ({
    id: record.id,
    ...parseCsvFields({ name, description: description ?? '' }),
    facets: record.facets,
    priceRanges: record.pricing.map(formatPriceRange),
    'Min quantity': minQuantity,
  })

export const formatPriceRange = (p: APIPriceRange) => ({
  maxQuantity: p.maxQuantity,
  fixedCost: p.fixedCost,
  variableCost: p.variableCost,
  fixedPrice: p.fixedPrice,
  variablePrice: p.variablePrice,
})
